import React, { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Container from '../../Container'
import PropTypes from 'prop-types'
import styles from '../form-controls.module.scss'
import HelperText from '../HelperText'
import IconButton from '../../IconButton'
import InputComponent from '../InputComponent'

const inputTypes = [
  window.HTMLInputElement,
  window.HTMLSelectElement,
  window.HTMLTextAreaElement,
]

const mergeRefs = (...refs) => {
  return (node) => {
    for (const ref of refs) {
      if (ref) {
        if (typeof ref === 'function') ref(node)
        if ('current' in ref) ref.current = node
      }
    }
  }
}

const TextInput = React.forwardRef(
  (
    {
      defaultValue = undefined,
      searchInput = false,
      autocomplete = 'on',
      cleanButton = false,
      revealButton = false,
      variant = 'outlined',
      className,
      focus = false,
      fullWidth,
      hidden,
      disabled,
      onChange = () => {},
      onPaste = () => {},
      label,
      error,
      placeholder,
      name,
      helperText,
      type = 'text',
      multiline = false,
      InputProps = {},
      value = undefined,
      onFocus = () => {},
      onBlur = () => {},
      onKeyDown = () => {},
      onKeyUp = () => {},
      readOnly = false,
      style,
      maxLength,
      autoFocus = false,
      prefix = null,
      simpleHelperStyle = false,
      inputMode = 'text',
      size = 'l',
    },
    ref,
  ) => {
    const inputRef = useRef(null)
    const [isFocused, setFocused] = useState(focus)
    const [isEmpty, setEmpty] = useState(false)
    const [defaultType, setDefaultType] = useState(type)

    useEffect(() => {
      if (inputRef?.current?.value) {
        setEmpty(!inputRef.current.value)
      } else {
        if (typeof defaultValue !== 'undefined') {
          setEmpty(!defaultValue)
        } else {
          setEmpty(!value)
        }
      }
    }, [defaultValue, value, inputRef])

    const onChangeHandle = useCallback(
      (e) => {
        onChange(e)
        setEmpty(e.currentTarget.value.length === 0)
      },
      [onChange, setEmpty],
    )

    const onPasteHandle = useCallback(
      (e) => {
        onPaste(e)
      },
      [onPaste],
    )

    const onFocusHandle = (e) => {
      if (readOnly) {
        e.preventDefault()
        return
      }
      setFocused(true)
      onFocus(e)
    }

    const onBlurHandle = (e) => {
      if (readOnly) {
        e.preventDefault()
        return
      }
      setFocused(false)
      onBlur(e)
    }

    const setFocus = useCallback(
      () => !readOnly && inputRef.current && inputRef.current.focus(),
      [inputRef, readOnly],
    )

    const cleanField = useCallback(() => {
      if (inputRef.current) {
        if (inputTypes.indexOf(inputRef.current.__proto__.constructor) > -1) {
          const setValue = Object.getOwnPropertyDescriptor(
            inputRef.current.__proto__,
            'value',
          ).set
          const event = new Event('input', { bubbles: true })

          setValue.call(inputRef.current, '')
          inputRef.current.dispatchEvent(event)
        }
      }
    }, [inputRef])

    const handleReveal = useCallback(() => {
      setDefaultType((defaultType) => (defaultType === type ? 'text' : type))
    }, [])

    const props = {
      size,
      name,
      disabled,
      hidden,
      value,
      onPaste: onPasteHandle,
      onChange: onChangeHandle,
      onFocus: onFocusHandle,
      onBlur: onBlurHandle,
      // onMouseDown: onMouseDownHandle,
      // onMouseUp: onMouseUpHandle,
      onKeyDown,
      onKeyUp,
      placeholder,
      type: defaultType,
      autoComplete: autocomplete,
      defaultValue,
      readOnly,
      maxLength,
      autoFocus,
      prefix,
      inputMode,
    }

    const startAdornment = InputProps.startAdornment ? (
      <div
        className={clsx(
          styles['control__adornment'],
          styles['control__adornment_start'],
        )}
        onClick={setFocus}
      >
        {InputProps.startAdornment}
      </div>
    ) : null

    const endAdornment =
      InputProps.endAdornment || cleanButton || revealButton ? (
        <div
          className={clsx(
            styles['control__adornment'],
            styles['control__adornment_end'],
          )}
        >
          {cleanButton && !isEmpty && (
            <IconButton
              name={'close'}
              size={size === 's' ? 's' : 'm'}
              bgColorHover={'none'}
              color={'Label-Secondary'}
              className={styles['control__cleanButton']}
              onClick={(e) => !disabled && cleanField(e)}
            />
          )}

          {revealButton && (
            <IconButton
              bgColorHover={'none'}
              name={defaultType === type ? 'eye-hidden' : 'eye-visible'}
              size={size === 's' ? 's' : 'm'}
              color={'Label-Secondary'}
              onClick={handleReveal}
            />
          )}

          {InputProps.endAdornment}
        </div>
      ) : null

    return (
      <span
        className={clsx(
          'control-component',
          styles['control-component'],
          styles[`control-component_variant_${variant}`],
          styles[`control-component_size_${size}`],
          {
            [styles['control-component_empty']]: isEmpty && !disabled,
            [styles['control-component_focused']]: isFocused || focus,
            [styles['control-component_error']]: error,
            [styles['control-component_fullWidth']]: fullWidth,
            [styles['control-component_withHelperText']]: helperText,
            [styles['control-component_hasAdornment']]:
              (Object.hasOwn(InputProps, 'startAdornment') &&
                !!InputProps.startAdornment) ||
              (Object.hasOwn(InputProps, 'endAdornment') &&
                !!InputProps.endAdornment),
            [styles['control-component_hasStartAdornment']]:
              Object.hasOwn(InputProps, 'startAdornment') &&
              !!InputProps.startAdornment,
            [styles['control-component_hasEndAdornment']]:
              Object.hasOwn(InputProps, 'endAdornment') &&
              !!InputProps.endAdornment,
            [styles['control-component_hasLabel']]: !!label,
            [styles['control-component_disabled']]: disabled,
            [styles['control-component_hidden']]: hidden,
            [styles['control-component_readonly']]: readOnly,
          },
          className,
        )}
        style={style}
      >
        <Container
          display={'flex'}
          component={'span'}
          className={clsx('control__wrapper', styles['control__wrapper'], {
            control__wrapper_hasError: error,
          })}
        >
          <div
            className={clsx(
              'control__controlWrapper',
              styles['control__controlWrapper'],
              {
                [styles['control__controlWrapper_default']]: !searchInput,
              },
            )}
          >
            {startAdornment}

            <Container
              className={styles['control__component-wrapper']}
              display={'flex'}
            >
              {label && (
                <label
                  className={clsx(styles['control__label'], {
                    [styles['control__label_withPrefix']]: !!prefix,
                    [styles['control__label_shrink']]: isFocused || !isEmpty,
                  })}
                >
                  {label}
                </label>
              )}
              {prefix && (
                <span className={styles['control__prefix']}>{prefix}</span>
              )}
              <InputComponent
                onClick={setFocus}
                multiline={multiline}
                InputProps={InputProps}
                ref={mergeRefs(ref, inputRef)}
                {...props}
              />
            </Container>

            {endAdornment}
          </div>

          {helperText && (
            <HelperText error={error} simpleHelperStyle={simpleHelperStyle}>
              {helperText}
            </HelperText>
          )}
        </Container>
      </span>
    )
  },
)

TextInput.propTypes = {
  cleanButton: PropTypes.bool,
  revealButton: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  fullWidth: PropTypes.bool,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hidden: PropTypes.bool,
  InputProps: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  multiline: PropTypes.bool,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'outlined']),
  value: PropTypes.any,
  name: PropTypes.string,
  type: PropTypes.oneOf(['text', 'number', 'email', 'password']),
  autocomplete: PropTypes.string,
  defaultValue: PropTypes.any,
  readOnly: PropTypes.bool,
  style: PropTypes.object,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPaste: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  simpleHelperStyle: PropTypes.bool,
  searchInput: PropTypes.bool,
  autoFocus: PropTypes.bool,
  inputMode: PropTypes.oneOf(['numeric', 'text', 'filter']),
  className: PropTypes.string,
  focus: PropTypes.bool,
  size: PropTypes.oneOf(['s', 'm', 'l']),
}

TextInput.displayName = 'TextInput'

export default TextInput
